import {
  Component,
  computed,
  inject,
  OnInit,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { SalesforceSMSData } from '../models/browser-events';
import { environment } from '../../environments/environment';
import { ChatStateService } from '../services/chat-state.service';
import { BrowserEventBusService } from '../services/event-bus.service';

@Component({
  selector: 'app-welcome',
  standalone: true,
  imports: [
    RouterModule,
    MatCardModule,
    CommonModule
  ],
  templateUrl: '../templates/welcome.component.html',
  styleUrls: ['../templates/welcome.scss'],
})
export class WelcomeComponent implements OnInit {
  private eventBus = inject(BrowserEventBusService);
  private router = inject(Router);

  public chatState = inject(ChatStateService);

  public example = `
    window.postMessage(
      {
        type: 'SMS_SF_DATA',
        data: {
          SenderName: 'John Doe',
          RecipientName: 'Jane Doe',
          RecipientPhone: '+15555555555',
          IsOptedOut: true, <-- optional, false if omitted
          Token: 'jwt content'
        }
      },
      window.origin
    );
  `;

  chatReady = computed(() => this.chatState.sender() && this.chatState.recipient() && this.chatState.token());

  ngOnInit(): void {
    console.log('WelcomeComponent initializing - iframe context:', window.parent !== window);
    console.log('Initial environment:', environment);
    console.log('Initial chat state ready:', this.chatReady());
    
    const isStandalone = this.eventBus.isStandalone();
    console.log('App running in standalone mode:', isStandalone);
    
    if (isStandalone) {
      // For standalone mode, bypass Salesforce data and use default/demo data
      console.log('Standalone mode - setting up demo data and initializing connections');
      this.setupStandaloneMode();
    } else {
      // For iframe mode, wait for Salesforce data
      this.eventBus.browserMessage$.subscribe(initPostMessage => {
        this.handlePostedMessage(initPostMessage.data.data);
      });
      
      // Send ready message with retry mechanism
      this.sendReadyMessage();
    }
    
    if (this.chatReady()) {
      this.router.navigate(['/messaging']);
    }
  }

  private setupStandaloneMode(): void {
    // Set up demo/default data for standalone testing
    this.chatState.sender.set({
      name: 'Demo User',
      phoneNumber: environment.messagingPhone,
    });
    
    this.chatState.recipient.set({
      name: 'Test Contact',
      phoneNumber: '+15555555555', // Demo number
    });
    
    // For standalone, you might want to use a demo token or handle auth differently
    const existingToken = localStorage.getItem('access-token');
    if (existingToken) {
      this.chatState.token.set(existingToken);
    } else {
      // You could set a demo token or handle authentication flow here
      console.warn('No access token found in standalone mode - authentication may be required');
    }
    
    // Navigate to messaging, which will handle WebSocket connections
    if (this.chatReady()) {
      this.router.navigate(['/messaging']);
    }
  }

  private sendReadyMessage(): void {
    this.eventBus.sendMessage({ type: 'SMS_SF_READY' });
    
    // Retry sending the ready message in case parent wasn't ready to receive it
    setTimeout(() => {
      if (!this.chatReady()) {
        console.log('Retrying SMS_SF_READY message...');
        this.eventBus.sendMessage({ type: 'SMS_SF_READY' });
      }
    }, 1000);
    
    // Final retry
    setTimeout(() => {
      if (!this.chatReady()) {
        console.log('Final retry of SMS_SF_READY message...');
        this.eventBus.sendMessage({ type: 'SMS_SF_READY' });
      }
    }, 3000);
  }



  handlePostedMessage(sfData: SalesforceSMSData) {
    console.log("Chat state before processing posted message:", {
      sender: this.chatState.sender(),
      recipient: this.chatState.recipient(),
      token: this.chatState.token(),
      isOptedOut: this.chatState.isOptedOut(),
      hasUnreadMessages: this.chatState.hasUnreadMessages()
    });
    
    if (!!sfData.Token && sfData.Token !== this.chatState.token()) {
      this.chatState.token.set(sfData.Token);
      localStorage.setItem('access-token', sfData.Token);
    }
    if (sfData.IsOptedOut !== this.chatState.isOptedOut()) {
      this.chatState.isOptedOut.set(sfData.IsOptedOut);
    }
    if (!!sfData.SenderName && sfData.SenderName != this.chatState.sender()?.name) {
      this.chatState.sender.set({
        name: sfData.SenderName ?? '',
        phoneNumber: environment.messagingPhone,
      });
    }
    if (!!sfData.RecipientName && sfData.RecipientPhone && (sfData.RecipientName !== this.chatState.recipient()?.name || sfData.RecipientPhone !== this.chatState.recipient()?.phoneNumber)) {
      this.chatState.recipient.set({
        name: sfData.RecipientName ?? '',
        phoneNumber: sfData.RecipientPhone,
      });
    }
    if(sfData.HasUnreadMessages !== this.chatState.hasUnreadMessages()) {
      this.chatState.hasUnreadMessages.set(!!sfData.HasUnreadMessages);
    }

    // Data is ready, messaging component will handle WebSocket connections

    if (this.chatReady() && this.router.url !== '/messaging') {
      this.router.navigate(['/messaging']);
    }

    console.log("Chat state after processing posted message:", {
      sender: this.chatState.sender(),
      recipient: this.chatState.recipient(),
      token: this.chatState.token(),
      isOptedOut: this.chatState.isOptedOut(),
      hasUnreadMessages: this.chatState.hasUnreadMessages()
    });
  }
}
